import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';

export default function Confirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/preorders/${id}`).then(r => setOrder(r.data)).catch(console.error);
  }, [id]);

  if (!order) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen py-12 md:py-20">
      <div className="container mx-auto px-4 md:px-6 max-w-2xl">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10" data-testid="confirmation-card">
          <div className="text-center mb-8">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}>
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto" />
            </motion.div>
            <h1 className="text-3xl font-bold text-slate-900 mt-4" data-testid="confirmation-title">{order.status === 'confirmee' ? 'Précommande confirmée !' : 'Demande enregistrée !'}</h1>
            <p className="text-slate-500 mt-2">{order.status === 'confirmee' ? `Merci ${order.first_name}, un email de confirmation vient de vous être envoyé.` : `Merci ${order.first_name}, votre demande a bien été prise en compte.`}</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-5">
            <h3 className="font-semibold text-sm text-slate-700 uppercase tracking-wider">Récapitulatif</h3>
            <Separator className="my-3" />
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">Extension</span><span className="font-medium">{order.extension_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Nom</span><span className="font-medium">{order.first_name} {order.last_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{order.email}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Téléphone</span><span className="font-medium">{order.phone}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Retrait</span><span className="font-medium">{order.delivery_method === 'retrait' ? 'En boutique' : 'Expédition'}</span></div>
            </div>
            <Separator className="my-3" />
            <h4 className="font-semibold text-sm text-slate-700 mb-2">Articles</h4>
            <div className="space-y-2">
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-600">{item.item_name} x{item.quantity}</span>
                  <span className="font-medium">{(item.unit_price * item.quantity).toFixed(2)} €</span>
                </div>
              ))}
            </div>
            <Separator className="my-3" />
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="text-violet-600">{order.total.toFixed(2)} €</span>
            </div>
          </div>

          {order.status === 'confirmee' ? (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex gap-3" data-testid="confirmation-info">
              <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-800">
                <p className="font-medium">C'est noté !</p>
                <p className="mt-1">Nous vous préviendrons par email dès l'arrivée des produits en boutique.</p>
              </div>
            </div>
          ) : (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3" data-testid="confirmation-info">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Information importante</p>
                <p className="mt-1">Les quantités TCG sont allouées à la main selon nos arrivages. Vous recevrez un email de confirmation une fois votre demande validée par notre équipe.</p>
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            <Link to="/catalogue">
              <Button className="rounded-full px-8" data-testid="back-to-catalog-btn">
                Continuer mes achats <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
